import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import Icon from '../components/ui/Icon';
import DropZone from '../components/tools/DropZone';
import FileList from '../components/tools/FileList';
import ProcessingView from '../components/tools/ProcessingView';
import { getToolById, TOOL_CATEGORIES, TOOLS } from '../data/tools';
import { filesActions, authActions } from '../store';

const ToolWorkspace = () => {
  const { toolId } = useParams();
  const dispatch = useDispatch();
  const tool = getToolById(toolId);
  const cat = tool ? TOOL_CATEGORIES[tool.category] : null;
  const { uploadedFiles } = useSelector(s => s.files);
  const { isAuthenticated, freeDocsUsed, freeDocsLimit } = useSelector(s => s.auth);
  const [phase, setPhase] = useState('upload'); // upload | settings | processing | done
  const [settings, setSettings] = useState({ quality: 'high', format: 'auto', ocr: false });

  if (!tool) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 opacity-30" />
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Tool not found</h2>
          <Link to="/tools" className="btn-primary inline-flex items-center gap-2 mt-4">
            <Icon name="ArrowLeft" size={16} /> Back to Tools
          </Link>
        </div>
      </div>
    );
  }

  const relatedTools = TOOLS.filter(t => t.category === tool.category && t.id !== tool.id).slice(0, 4);

  const handleStartProcessing = () => {
    if (!isAuthenticated && freeDocsUsed >= freeDocsLimit) {
      dispatch(authActions.setShowAuthModal(true));
      return;
    }
    setPhase('processing');
  };

  const handleComplete = () => {
    setPhase('done');
    dispatch(filesActions.addToHistory({
      toolId: tool.id,
      toolName: tool.name,
      fileName: uploadedFiles[0]?.name || 'document',
      category: tool.category,
    }));
  };

  const handleReset = () => {
    setPhase('upload');
    dispatch(filesActions.clearFiles());
  };

  return (
    <div className="min-h-screen pt-20 pb-16" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex items-center gap-2 text-sm mb-8 pt-4" style={{ color: 'var(--text-muted)' }}>
          <Link to="/" className="hover:text-brand-600 transition-colors">Home</Link>
          <Icon name="ChevronRight" size={14} />
          <Link to="/tools" className="hover:text-brand-600 transition-colors">Tools</Link>
          <Icon name="ChevronRight" size={14} />
          <span style={{ color: cat?.color }}>{tool.name}</span>
        </motion.div>

        {/* Tool Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10">
          <div className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4"
            style={{ background: cat?.bgMedium, color: cat?.color }}>
            <Icon name={tool.icon} size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {tool.name}
          </h1>
          <p className="text-base max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
            {tool.desc}
          </p>
        </motion.div>

        {/* Step Indicator */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex items-center justify-center gap-3 mb-10">
          {['Upload', 'Settings', 'Process', 'Download'].map((step, i) => {
            const phaseIdx = ['upload', 'settings', 'processing', 'done'].indexOf(phase);
            const isActive = i === phaseIdx;
            const isComplete = i < phaseIdx;
            return (
              <React.Fragment key={step}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300`}
                    style={{
                      background: isComplete ? '#10b981' : isActive ? cat?.color : 'var(--bg-tertiary)',
                      color: isComplete || isActive ? 'white' : 'var(--text-muted)',
                    }}>
                    {isComplete ? <Icon name="Check" size={14} /> : i + 1}
                  </div>
                  <span className={`hidden sm:inline text-sm font-medium ${isActive ? 'font-bold' : ''}`}
                    style={{ color: isActive ? cat?.color : 'var(--text-muted)' }}>
                    {step}
                  </span>
                </div>
                {i < 3 && (
                  <div className="w-12 h-0.5 rounded-full"
                    style={{ background: isComplete ? '#10b981' : 'var(--border-light)' }} />
                )}
              </React.Fragment>
            );
          })}
        </motion.div>

        {/* Phase: Upload */}
        {phase === 'upload' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <DropZone onFilesAdded={() => setPhase('settings')} />
            <FileList />

            {uploadedFiles.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="mt-6 text-center">
                <button onClick={() => setPhase('settings')} className="btn-primary !px-8">
                  Continue <Icon name="ArrowRight" size={16} className="ml-2" />
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Phase: Settings */}
        {phase === 'settings' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto">
            <div className="card p-6">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Icon name="Settings" size={20} style={{ color: cat?.color }} />
                Conversion Settings
              </h3>

              {/* Files summary */}
              <div className="mb-6 p-3 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="FileText" size={16} style={{ color: cat?.color }} />
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
              </div>

              {/* Quality */}
              <div className="mb-5">
                <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>Output Quality</label>
                <div className="grid grid-cols-3 gap-2">
                  {['low', 'medium', 'high'].map(q => (
                    <button key={q} onClick={() => setSettings(s => ({ ...s, quality: q }))}
                      className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all capitalize ${
                        settings.quality === q ? 'text-white' : ''
                      }`}
                      style={{
                        background: settings.quality === q ? cat?.color : 'var(--bg-secondary)',
                        color: settings.quality === q ? 'white' : 'var(--text-secondary)',
                        border: `1px solid ${settings.quality === q ? cat?.color : 'var(--border-light)'}`,
                      }}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              {/* OCR Toggle */}
              <div className="mb-6">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-semibold block" style={{ color: 'var(--text-primary)' }}>Enable OCR</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Extract text from scanned images</span>
                  </div>
                  <div onClick={() => setSettings(s => ({ ...s, ocr: !s.ocr }))}
                    className="w-11 h-6 rounded-full relative transition-colors cursor-pointer"
                    style={{ background: settings.ocr ? cat?.color : 'var(--border-light)' }}>
                    <div className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform"
                      style={{ left: settings.ocr ? '24px' : '4px' }} />
                  </div>
                </label>
              </div>

              {/* Save to cloud */}
              <div className="mb-8 p-4 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Icon name="Cloud" size={16} />
                  Save to Cloud (optional)
                </h4>
                <div className="flex gap-2">
                  {['Google Drive', 'Dropbox', 'OneDrive'].map(s => (
                    <button key={s} className="tool-chip text-xs">{s}</button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button onClick={() => setPhase('upload')} className="btn-secondary flex-1">
                  <Icon name="ArrowLeft" size={16} className="mr-2" /> Back
                </button>
                <button onClick={handleStartProcessing}
                  className="btn-primary flex-1"
                  style={{ background: `linear-gradient(135deg, ${cat?.color}, ${cat?.color}cc)` }}>
                  <Icon name="Zap" size={16} className="mr-2" />
                  Convert Now
                </button>
              </div>

              {/* Free usage notice */}
              {!isAuthenticated && (
                <p className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                  {freeDocsUsed}/{freeDocsLimit} free conversions used.{' '}
                  <button onClick={() => dispatch(authActions.setShowAuthModal(true))}
                    className="underline font-medium" style={{ color: '#3374ff' }}>
                    Sign in for unlimited
                  </button>
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Phase: Processing */}
        {phase === 'processing' && (
          <ProcessingView
            fileName={uploadedFiles[0]?.name}
            toolName={tool.name}
            onComplete={handleComplete}
          />
        )}

        {/* Phase: Done (handled in ProcessingView, but add reset) */}
        {phase === 'done' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mt-8">
            <button onClick={handleReset} className="btn-secondary inline-flex items-center gap-2">
              <Icon name="RefreshCw" size={16} /> Convert Another File
            </button>
          </motion.div>
        )}

        {/* Related Tools */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="mt-20 pt-10" style={{ borderTop: '1px solid var(--border-light)' }}>
          <h3 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Related Tools
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedTools.map(t => (
              <motion.div key={t.id} whileHover={{ y: -2 }}>
                <Link to={`/tool/${t.id}`} onClick={handleReset}>
                  <div className="card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: cat?.bgLight, color: cat?.color }}>
                      <Icon name={t.icon} size={16} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t.name}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ToolWorkspace;
